'use client';

import * as React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useStats } from '@/hooks/useStats';
import { User } from '@/lib/supabase';
import { Trophy, Medal, Award } from 'lucide-react';
import { LeaderBoardRowSkeleton } from './StatCardSkeleton';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const Avatar: React.FC<{ src: string | null; alt: string; size?: 'sm' | 'md' }> = ({ src, alt, size = 'md' }) => {
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  const pixelSize = size === 'sm' ? 32 : 40;
  const initials = alt.slice(0, 2).toUpperCase();

  return src ? (
    <Image
      src={src}
      alt={alt}
      width={pixelSize}
      height={pixelSize}
      className={`${sizeClasses} rounded-full object-cover`}
    />
  ) : (
    <div className={`${sizeClasses} rounded-full bg-gray-700 flex items-center justify-center font-semibold text-gray-300`}>
      {initials}
    </div>
  );
};

const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
  if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />;
  return <span className="text-gray-400 font-semibold">#{rank}</span>;
};

const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[...Array(10)].map((_, i) => (
      <LeaderBoardRowSkeleton key={i} />
    ))}
  </div>
);

const EmptyState: React.FC = () => (
  <div className="text-center py-12">
    <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
    <p className="text-gray-400">No contributors found</p>
  </div>
);

interface StatsWithContributors {
  topContributors?: User[];
}

function LeaderBoardInner() {
  const { stats, isLoading, error } = useStats();

  // Memoize topContributors to avoid unnecessary re-renders
  const topContributors = React.useMemo(() => {
    return (stats as StatsWithContributors)?.topContributors || [];
  }, [stats]);

  // Memoize the sliced contributors list to avoid unnecessary array operations
  const displayContributors = React.useMemo(() => {
    return topContributors.slice(0, 10);
  }, [topContributors]);

  if (isLoading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Top Contributors</h2>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Top Contributors</h2>
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Unable to load leaderboard</p>
        </div>
      </div>
    );
  }

  if (!topContributors || topContributors.length === 0) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Top Contributors</h2>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
      <h2 className="text-xl font-bold text-gray-100 mb-4">Top Contributors</h2>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-2 text-sm font-semibold text-gray-400">Rank</th>
              <th className="text-left py-3 px-2 text-sm font-semibold text-gray-400">User</th>
              <th className="text-right py-3 px-2 text-sm font-semibold text-gray-400">Reputation</th>
              <th className="text-right py-3 px-2 text-sm font-semibold text-gray-400">Badges</th>
            </tr>
          </thead>
          <tbody>
            {displayContributors.map((user, index) => (
              <tr
                key={user.id}
                className="border-b border-gray-700/50 hover:bg-gray-700/50 transition-colors"
              >
                <td className="py-3 px-2">
                  <div className="flex items-center justify-start">
                    <RankBadge rank={index + 1} />
                  </div>
                </td>
                <td className="py-3 px-2">
                  <Link
                    href={`/users/${user.username}`}
                    className="flex items-center gap-3 hover:text-blue-400 transition-colors"
                  >
                    <Avatar src={user.avatar_url} alt={user.username} />
                    <span className="font-medium text-gray-100">{user.username}</span>
                  </Link>
                </td>
                <td className="py-3 px-2 text-right text-gray-100 font-semibold">
                  {formatNumber(user.reputation)}
                </td>
                <td className="py-3 px-2 text-right text-gray-400">
                  {user.badges.length}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile List */}
      <div className="md:hidden space-y-3">
        {displayContributors.map((user, index) => (
          <Link
            key={user.id}
            href={`/users/${user.username}`}
            className="block bg-gray-700/30 hover:bg-gray-700/50 rounded-lg p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <RankBadge rank={index + 1} />
              <Avatar src={user.avatar_url} alt={user.username} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-100 truncate">{user.username}</p>
                <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                  <span>{formatNumber(user.reputation)} rep</span>
                  <span>{user.badges.length} badges</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function LeaderBoard() {
  return (
    <ErrorBoundary
      fallback={
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Top Contributors</h2>
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">Failed to load leaderboard</p>
          </div>
        </div>
      }
    >
      <LeaderBoardInner />
    </ErrorBoundary>
  );
}
